<template>
  <div>
    <!-- 경고표지 요소 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 경고표지 요소</h5>

      <div class="w-full" style="overflow-x: auto">
        <table class="ghs-table">
          <colgroup>
            <col v-for="p in pictogramList" :key="`col_${p.key}`" />
            <col width="250" />
          </colgroup>
          <tbody>
            <tr>
              <th colspan="9">그림문자</th>
              <th rowspan="2" class="signal-cell">신호어</th>
            </tr>
            <tr>
              <th v-for="p in pictogramList" :key="`label_${p.key}`">
                <vs-checkbox v-model="form.그림문자[p.key]" class="custom-checkbox" :disabled="isPrint || isReadonly">
                  <span class="px-2">{{ p.label }}</span>
                </vs-checkbox>
              </th>
            </tr>
            <tr>
              <td v-for="p in pictogramList" :key="`img_${p.key}`">
                <img :src="getPictogramImg(p.key)" class="pictogram-img" :alt="p.label" />
                <p class="pictogram-code">{{ p.key }}</p>
              </td>
              <td>
                <div class="flex gap-4 justify-center items-center">
                  <vs-radio v-for="opt in 신호어options" :key="opt" class="signal-option" v-model="form.신호어" vs-name="신호어" :vs-value="opt" :disabled="isPrint || isReadonly">{{ opt }}</vs-radio>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 경고문구 -->
    <div class="mt-28px">
      <vs-row class="mb-4 center-flex">
        <h5><i class="dot"></i> 경고문구</h5>
        <div class="ml-auto" v-if="!isPrint">
          <vs-button color="secondary" type="border" @click="handleRemoveChecked">선택삭제</vs-button>
        </div>
      </vs-row>

      <div class="warning-statement-grid">
        <!-- 유해ㆍ위험문구 -->
        <div class="w-full" style="overflow-x: auto">
          <table>
            <colgroup>
              <col v-if="!isPrint" width="60" />
              <col width="100" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th colspan="3">유해·위험문구 <vs-button class="py-1 px-2" color="secondary" type="border" @click="openSearchPopup('유해위험문구')">찾기</vs-button></th>
              </tr>
            </thead>
            <tbody is="draggable" :list="form.유해위험문구" tag="tbody" handle=".handle">
              <tr v-for="(row, index) in form.유해위험문구" :key="index">
                <td v-if="!isPrint">
                  <div class="flex items-center justify-center">
                    <feather-icon class="handle" icon="MenuIcon" style="width: 14px; height: 14px; cursor: pointer" />
                    <vs-checkbox v-model="row.is_checked" class="custom-checkbox"></vs-checkbox>
                  </div>
                </td>
                <td>{{ row.코드 }}</td>
                <td>{{ row.문구 }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 예방조치문구 -->
        <div class="w-full" style="overflow-x: auto">
          <table>
            <colgroup>
              <col v-if="!isPrint" width="60" />
              <col width="100" />
              <col width="90" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th colspan="4">예방조치문구 <vs-button class="py-1 px-2" color="secondary" type="border" @click="openSearchPopup('예방조치문구')">찾기</vs-button></th>
              </tr>
            </thead>
            <tbody is="draggable" :list="form.예방조치문구" tag="tbody" handle=".handle">
              <tr v-for="(row, index) in form.예방조치문구" :key="index">
                <td v-if="!isPrint">
                  <div class="flex items-center justify-center">
                    <feather-icon class="handle" icon="MenuIcon" style="width: 14px; height: 14px; cursor: pointer" />
                    <vs-checkbox v-model="row.is_checked" class="custom-checkbox"></vs-checkbox>
                  </div>
                </td>
                <td>{{ row.코드 }}</td>
                <td>{{ row.구분 }}</td>
                <td>{{ row.문구 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <popup-msds-search v-model="showSearchPopup" @select="onSelectMsds" />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import PopupMsdsSearch from "./PopupMSDSSearch.vue";
import { GHS_PICTOGRAM_LIST, getPictogramImg } from "./ghsConstants";

const 그림문자기본값 = () => ({
  GHS01: false,
  GHS02: false,
  GHS03: false,
  GHS04: false,
  GHS05: false,
  GHS06: false,
  GHS07: false,
  GHS08: false,
  GHS09: false,
});

export default {
  name: "GHSWarningLabel",
  components: {
    draggable,
    PopupMsdsSearch,
  },
  props: {
    value: Object,
    isPrint: Boolean,
    isReadonly: Boolean,
  },
  data() {
    return {
      showSearchPopup: false,
      searchTarget: "유해위험문구",
      신호어options: ["없음", "경고", "위험"],
      pictogramList: GHS_PICTOGRAM_LIST,
      form: {
        그림문자: 그림문자기본값(),
        신호어: "없음",
        유해위험문구: [],
        예방조치문구: [],
      },
    };
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          const keys = Object.keys(value);
          for (const key of keys) {
            if (value[key]) {
              this.form[key] = value[key];
            }
          }
        }
      },
    },
    form: {
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    getPictogramImg,
    openSearchPopup(target) {
      this.searchTarget = target;
      this.showSearchPopup = true;
    },
    onSelectMsds(data) {
      const newRows = data[this.searchTarget];
      if (!newRows || newRows.length === 0) return;
      this.form[this.searchTarget].push(...newRows);
    },
    handleRemoveChecked() {
      for (const field of ["유해위험문구", "예방조치문구"]) {
        for (let i = 0; i < this.form[field].length; i++) {
          if (this.form[field][i].is_checked) {
            this.form[field].splice(i, 1);
            i = -1;
          }
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.custom-checkbox::v-deep {
  justify-content: center !important;

  .vs-checkbox {
    margin: 0 !important;
  }
}

.table-caption {
  font-weight: 700;
  font-size: 14px;
}

.warning-statement-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }

  table th {
    padding: 8px 11px;
  }

  table td {
    padding: 18px 0;
    word-break: break-all;
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th {
    font-weight: 700;
    font-size: 14px;
    text-align: center;
    background-color: #f9f9f9;
    border: 1px solid #cdcdcd;
    padding: 20px 11px;
  }

  td {
    font-weight: 400;
    font-size: 14px;
    text-align: center;
    border: 1px solid #cdcdcd;
    padding: 7px;
  }
}

.ghs-table {
  td {
    padding: 10px 4px;
  }

  .pictogram-img {
    width: 100%;
    height: 84px;
    object-fit: contain;
  }

  .pictogram-code {
    margin-top: 8px;

    font-weight: 500;
    font-size: 15px;
    vertical-align: middle;
  }

  .signal-cell {
    vertical-align: middle;
    padding: 10px;
  }

  .signal-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .signal-title {
    font-weight: 700;
    font-size: 14px;
  }

  .signal-options {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
